const SHARE_TYPES = {
  email: 'email',
  clipboard: 'clipboard',
};

const FEEDBACK_DURATION = 2000;

/**
 * Get share URL from component or fallback to current URL
 * @param {HTMLElement} root
 * @returns {string}
 */
const getShareUrl = (root) => {
  const dataUrl = root.dataset.url;

  return dataUrl && dataUrl !== '' ? dataUrl : window.location.href;
};

/**
 * Get share title from component or fallback to document title
 * @param {HTMLElement} root
 * @returns {string}
 */
const getShareTitle = (root) => {
  const dataTitle = root.dataset.title;

  return dataTitle && dataTitle !== '' ? dataTitle : document.title;
};

/**
 * Share via email using mailto
 * @param {string} url
 * @param {string} title
 */
const shareByEmail = (url, title) => {
  const subject = encodeURIComponent(title);
  const body = encodeURIComponent(url);

  window.location.href = `mailto:?subject=${subject}&body=${body}`;
};

/**
 * Copy link to clipboard using the modern Clipboard API
 * @param {string} url
 * @param {HTMLElement} item
 */
const copyToClipboard = async (url, item) => {
  try {
    await navigator.clipboard.writeText(url);
    showCopyFeedback(item, true);
  } catch {
    showCopyFeedback(item, false);
  }
};

/**
 * Show visual feedback after copy attempt
 * @param {HTMLElement} item
 * @param {boolean} success
 */
const showCopyFeedback = (item, success) => {
  const feedbackClass = success ? '-copied' : '-error';

  item.classList.add(feedbackClass);

  setTimeout(() => {
    item.classList.remove(feedbackClass);
  }, FEEDBACK_DURATION);
};

/**
 * Handle share action based on type
 * @param {string} type
 * @param {HTMLElement} root
 * @param {HTMLElement} item
 */
const handleShare = (type, root, item) => {
  const url = getShareUrl(root);
  const title = getShareTitle(root);

  switch (type) {
    case SHARE_TYPES.email:
      shareByEmail(url, title);
      break;
    case SHARE_TYPES.clipboard:
      copyToClipboard(url, item);
      break;
  }
};

/**
 * Toggle dropdown menu
 * @param {HTMLElement} root
 * @param {HTMLElement} cta
 */
const toggleMenu = (root, cta) => {
  const isOpen = root.classList.toggle('-open');

  cta.setAttribute('aria-expanded', String(isOpen));
  cta.blur();
};

// Module-level: single document listener for all instances (prevents memory leaks)
let documentListenerInitialized = false;

const initDocumentClickListener = () => {
  if (documentListenerInitialized) {
    return;
  }

  document.addEventListener('click', (event) => {
    const openShares = document.querySelectorAll('.js-socialShare.-open');

    for (const share of openShares) {
      if (!share.contains(event.target)) {
        const cta = share.querySelector('.js-socialShare-cta');

        share.classList.remove('-open');

        if (cta) {
          cta.setAttribute('aria-expanded', 'false');
        }
      }
    }
  }, { passive: true });

  documentListenerInitialized = true;
};

/**
 * Handle keyboard navigation
 * @param {HTMLElement} root
 * @param {HTMLElement} cta
 */
const handleKeyboard = (root, cta) => {
  root.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && root.classList.contains('-open')) {
      root.classList.remove('-open');
      cta.setAttribute('aria-expanded', 'false');
      cta.focus();
    }
  });
};

const SocialShare = () => {
  const roots = document.querySelectorAll('.js-socialShare');

  if (!roots || roots.length === 0) {
    return;
  }

  Array.from(roots).forEach((root) => {
    const cta = root.querySelector('.js-socialShare-cta');
    const items = root.querySelectorAll('.js-socialShare-item');

    if (cta) {
      cta.addEventListener('click', () => toggleMenu(root, cta));
      // click outside handled by module-level listener
      handleKeyboard(root, cta);
    }

    if (items && items.length > 0) {
      Array.from(items).forEach((item) => {
        const button = item.querySelector('button');
        const type = item.dataset.type;

        if (button && type) {
          button.addEventListener('click', () => {
            handleShare(type, root, item);
            toggleMenu(root, cta);
          });
        }
      });
    }
  });

  // Initialize single document listener for all socialShare instances
  initDocumentClickListener();
};

export default SocialShare;
